/**
 * SportProduct — Page produit (full-page, plus en modal).
 * Route : /sport/boutique/produit/:id
 * Reprend le contenu de ProductDetailModal en layout page complet.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Tag, Truck, Shield, ChevronLeft, Check } from "lucide-react";
import { api } from "../../lib/api";
import { useCart } from "../../lib/useCart";
import { getProductSizes } from "../../lib/productSizes";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#0D0220";

function priceFmt(cents) {
  const v = (cents || 0) / 100;
  return v.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
}

export default function SportProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");

  const sizes = useMemo(() => getProductSizes(product), [product]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setSelectedSize("");
    api.get(`/sport/products/${id}`)
      .then((r) => setProduct(r.data?.product || r.data || null))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));    // Related products (same category or first 4)
    api.get(`/sport/products`)
      .then((r) => {
        const list = r.data || [];
        setRelated(list.filter((p) => p.id !== id).slice(0, 4));
      }).catch(() => {});
    // Scroll top on mount
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const handleAdd = () => {
    if (!product) return;
    if (sizes && !selectedSize) {
      toast.error("Choisissez une taille avant d'ajouter au panier");
      return;
    }
    add(
      {
        id: product.id,
        slug: product.id,
        name: product.name,
        price_cents: product.price,
        featured_image: product.image,
        stock_tracking: false,
        stock_qty: 99,
      },
      1,
      selectedSize ? { id: selectedSize, options: { Taille: selectedSize } } : null,
    );
    setAdded(true);
    toast.success(`${product.name}${selectedSize ? ` · ${selectedSize}` : ""} ajouté au panier`);
    setTimeout(() => setAdded(false), 2500);
  };

  if (loading) {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: BG_DEEP }}>
        <div className="text-white/60 text-sm uppercase tracking-widest">Chargement…</div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center px-4" style={{ background: BG_DEEP }}>
        <div className="text-white/80 text-lg font-bold mb-3">Produit introuvable</div>
        <Link to="/sport/boutique" className="px-5 py-2.5 rounded-full text-white font-black uppercase text-xs tracking-wider"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
          Retour boutique
        </Link>
      </div>
    );
  }

  const inEur = priceFmt(product.price);
  const oldEur = product.oldPrice ? priceFmt(product.oldPrice) : null;

  return (
    <div style={{ background: BG_DEEP }} className="min-h-svh">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4 text-xs uppercase tracking-wider text-white/50">
          <Link to="/sport/boutique" className="hover:text-pink-300 inline-flex items-center gap-1" data-testid="product-back">
            <ChevronLeft size={14}/> Boutique
          </Link>
          <span>/</span>
          <span className="text-white/80 truncate max-w-[200px]">{product.name}</span>
        </div>

        {/* Carte produit */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row"
          data-testid="product-page-card"
        >
          {/* Image */}
          <div className="relative w-full lg:w-1/2 bg-slate-50 flex items-center justify-center p-8 lg:p-12 overflow-hidden">
            {product.sale && (
              <div className="absolute top-5 left-5 px-3 py-1 rounded-full font-black text-[11px] tracking-widest uppercase shadow-xl"
                style={{ background: SECONDARY, color: "white" }}>
                -{product.sale_pct || 20}%
              </div>
            )}
            <img src={product.image} alt={product.name} className="max-w-full max-h-[420px] object-contain"/>
          </div>

          {/* Détails */}
          <div className="w-full lg:w-1/2 p-6 sm:p-10 flex flex-col">
            {product.brand && (
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">{product.brand}</div>
            )}
            <h1 className="font-black uppercase italic leading-tight" style={{ color: PRIMARY, fontSize: "clamp(1.6rem, 3vw, 2.5rem)", fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
              {product.name}
            </h1>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-black text-4xl tabular-nums" style={{ color: SECONDARY, fontFamily: "'Bebas Neue', sans-serif" }}>{inEur}</span>
              {oldEur && <span className="text-base text-slate-400 line-through tabular-nums">{oldEur}</span>}
            </div>

            {product.soldOut && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 text-[11px] font-black uppercase tracking-widest w-max">
                <span className="w-2 h-2 rounded-full bg-rose-500"/> Épuisé
              </div>
            )}

            <div className="mt-6 space-y-2.5 text-sm text-slate-600 border-y border-slate-100 py-4">
              <div className="flex items-center gap-2">
                <Tag size={15} className="text-pink-500 flex-shrink-0"/>
                <span><strong className="text-slate-800">Marque :</strong> {product.brand || "Officiel"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck size={15} className="text-pink-500 flex-shrink-0"/>
                <span>Livraison 7-10 jours ouvrés</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={15} className="text-pink-500 flex-shrink-0"/>
                <span>Produit officiel — soutien direct au club</span>
              </div>
            </div>

            <p className="text-slate-600 text-sm mt-5 leading-relaxed">
              {product.description || `Produit officiel de la boutique Istres Provence Handball. Chaque commande soutient directement le club et sa formation.`}
            </p>

            {/* Sélecteur de taille — affiché uniquement si le produit a des tailles */}
            {sizes && (
              <div className="mt-6" data-testid="product-size-picker">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-slate-700 font-black">Taille</span>
                  {selectedSize && (
                    <span className="text-[11px] font-bold" style={{ color: SECONDARY }}>{selectedSize}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((s) => {
                    const active = selectedSize === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSelectedSize(s)}
                        data-testid={`size-${s}`}
                        className={`min-w-[44px] px-3 h-10 rounded-lg font-black text-xs uppercase tracking-wider transition border-2 ${active ? "text-white shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-pink-400 hover:text-pink-600"}`}
                        style={active ? { background: SECONDARY, borderColor: SECONDARY } : undefined}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-auto pt-6">
              {!product.soldOut ? (
                <button
                  data-testid="product-page-add-to-cart"
                  onClick={handleAdd}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-xl hover:scale-[1.02] active:scale-[0.99] transition"
                  style={{ background: added ? `linear-gradient(135deg, #16a34a, #22c55e)` : `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                >
                  {added ? (<><Check size={16}/> Ajouté au panier !</>) : (<><ShoppingBag size={16}/> Ajouter au panier</>)}
                </button>
              ) : (
                <button disabled className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-black uppercase text-sm tracking-widest text-white bg-slate-400 cursor-not-allowed">
                  Rupture de stock
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-white font-black uppercase italic text-2xl mb-5" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>
              Vous aimerez aussi
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {related.map((p) => (
                <button key={p.id} type="button" onClick={() => navigate(`/sport/boutique/produit/${p.id}`)}
                  className="bg-white rounded-2xl p-4 shadow-md hover:shadow-xl hover:scale-[1.02] transition text-left">
                  <div className="aspect-square bg-slate-50 rounded-xl flex items-center justify-center overflow-hidden mb-3">
                    <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain"/>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{p.brand}</div>
                  <div className="font-black text-sm leading-tight line-clamp-2 mb-2" style={{ color: PRIMARY }}>{p.name}</div>
                  <div className="font-black tabular-nums" style={{ color: SECONDARY, fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.25rem" }}>{priceFmt(p.price)}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
